<template>
  <div
    :data-testid="`${testIdPrefix}__sort-dropdown`"
    class="consolidated-results__sort-dropdown"
  >
    <div class="consolidated-results__sort-dropdown-header">
      <h4 class="consolidated-results__sort-dropdown-title">Sort Columns</h4>
      <button
        type="button"
        :data-testid="`${testIdPrefix}__sort-dropdown-close`"
        class="consolidated-results__sort-dropdown-close"
        @click="$emit('close')"
      >
        <svg class="consolidated-results__sort-dropdown-close-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div
      v-if="enableSavedFilterSort"
      class="consolidated-results__tabs consolidated-results__tabs--dropdown"
    >
      <button
        type="button"
        :data-testid="`${testIdPrefix}__sort-tab-saved`"
        :class="['consolidated-results__tab consolidated-results__tab--small', activeSortTab === 'saved' ? 'consolidated-results__tab--active' : '']"
        @click="$emit('update:active-sort-tab', 'saved')"
      >
        Saved Sort
      </button>
      <button
        type="button"
        :data-testid="`${testIdPrefix}__sort-tab-playground`"
        :class="['consolidated-results__tab consolidated-results__tab--small', activeSortTab === 'playground' ? 'consolidated-results__tab--active' : '']"
        @click="$emit('update:active-sort-tab', 'playground')"
      >
        Sort
      </button>
    </div>

    <transition name="tab-slide" mode="out-in">
      <div :key="effectiveSortTab" class="consolidated-results__sort-dropdown-body">
        <div class="sort-builder__search-wrap">
          <input
            v-model="sortSearch"
            type="text"
            :data-testid="`${testIdPrefix}__sort-search`"
            class="sort-builder__search"
            placeholder="Search columns"
          />
        </div>
        <div class="sort-builder__list">
          <div
            v-for="column in filteredSortColumns"
            :key="column.key"
            :data-testid="`${testIdPrefix}__sort-item`"
            class="sort-builder__item"
          >
            <div class="sort-builder__item-row">
              <div class="sort-builder__checkbox-wrapper">
                <input
                  :id="sortCheckboxId(column.key)"
                  type="checkbox"
                  :data-testid="`${testIdPrefix}__sort-checkbox-${column.key}`"
                  class="sort-builder__checkbox"
                  :checked="isSortColumnSelected(column.key)"
                  @change="$emit('toggle-sort-column', column.key)"
                />
                <label :for="sortCheckboxId(column.key)" class="sort-builder__checkbox-label">
                  {{ column.label }}
                </label>
              </div>

              <div v-if="isSortColumnSelected(column.key)" class="sort-builder__controls">
                <span
                  :data-testid="`${testIdPrefix}__sort-order-${column.key}`"
                  class="sort-builder__order-badge"
                >
                  {{ getSortOrder(column.key) + 1 }}
                </span>
                <div class="sort-builder__direction-toggle">
                  <button
                    type="button"
                    :data-testid="`${testIdPrefix}__sort-asc-${column.key}`"
                    :class="['sort-builder__direction-btn', getSortDirection(column.key) === 'asc' ? 'sort-builder__direction-btn--active' : '']"
                    @click="$emit('set-sort-direction', column.key, 'asc')"
                  >
                    Asc
                  </button>
                  <button
                    type="button"
                    :data-testid="`${testIdPrefix}__sort-desc-${column.key}`"
                    :class="['sort-builder__direction-btn', getSortDirection(column.key) === 'desc' ? 'sort-builder__direction-btn--active' : '']"
                    @click="$emit('set-sort-direction', column.key, 'desc')"
                  >
                    Desc
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="sort-builder__actions">
          <button
            type="button"
            :data-testid="`${testIdPrefix}__reset-sort-btn`"
            class="filter-builder__reset-btn"
            @click="$emit('reset-sort')"
          >
            Reset
          </button>
          <button
            type="button"
            :data-testid="`${testIdPrefix}__apply-sort-btn`"
            class="filter-builder__apply-btn"
            :disabled="currentSortItems.length === 0"
            @click="$emit('apply-sort')"
          >
            Apply
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'TMResultsSortDropdown',

  props: {
    testIdPrefix: {
      type: String,
      required: true,
    },
    sortBtnTestId: {
      type: String,
      required: true,
    },
    activeSortTab: {
      type: String,
      default: 'saved',
    },
    availableSortColumns: {
      type: Array,
      default: () => [],
    },
    savedSort: {
      type: Array,
      default: () => [],
    },
    playgroundSort: {
      type: Array,
      default: () => [],
    },
    enableSavedFilterSort: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      clickOutsideTimeoutId: null,
      sortSearch: '',
    };
  },

  computed: {
    effectiveSortTab() {
      return this.enableSavedFilterSort ? this.activeSortTab : 'playground';
    },

    currentSortItems() {
      const items = this.effectiveSortTab === 'saved' ? this.savedSort : this.playgroundSort;
      return Array.isArray(items) ? items : [];
    },

    filteredSortColumns() {
      const term = (this.sortSearch || '').trim().toLowerCase();
      if (!term) return this.availableSortColumns;
      return (this.availableSortColumns || []).filter((column) => {
        const label = String(column.label || column.key || '').toLowerCase();
        return label.includes(term);
      });
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.positionDropdown();
      this.clickOutsideTimeoutId = setTimeout(() => {
        this.clickOutsideTimeoutId = null;
        document.addEventListener('click', this.handleClickOutside);
      }, 100);
    });
  },

  beforeDestroy() {
    if (this.clickOutsideTimeoutId != null) {
      clearTimeout(this.clickOutsideTimeoutId);
      this.clickOutsideTimeoutId = null;
    }
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    sortCheckboxId(columnKey) {
      const safeKey = String(columnKey ?? '').replace(/\s+/g, '-');
      return `${this.testIdPrefix}__sort-checkbox-input-${safeKey}`;
    },

    isSortColumnSelected(columnKey) {
      return this.currentSortItems.some((item) => item.key === columnKey);
    },

    getSortOrder(columnKey) {
      const index = this.currentSortItems.findIndex((item) => item.key === columnKey);
      return index >= 0 ? index : null;
    },

    getSortDirection(columnKey) {
      const item = this.currentSortItems.find((entry) => entry.key === columnKey);
      return item ? item.direction : null;
    },

    positionDropdown() {
      const sortBtn = document.querySelector(`[data-testid="${this.sortBtnTestId}"]`);
      const dropdown = document.querySelector(`[data-testid="${this.testIdPrefix}__sort-dropdown"]`);
      if (sortBtn && dropdown) {
        const btnRect = sortBtn.getBoundingClientRect();
        dropdown.style.top = `${btnRect.bottom + 8}px`;
        dropdown.style.left = `${btnRect.left}px`;
      }
    },

    handleClickOutside(event) {
      const dropdown = document.querySelector(`[data-testid="${this.testIdPrefix}__sort-dropdown"]`);
      const sortBtn = document.querySelector(`[data-testid="${this.sortBtnTestId}"]`);
      if (dropdown && !dropdown.contains(event.target) && sortBtn && !sortBtn.contains(event.target)) {
        this.$emit('close');
      }
    },
  },
};
</script>
